<template>
    <div class="ts-overflow-scroll ts-max-h-screen p-4 bg-gray-50 rounded-lg shadow-sm">

        <div class="d-flex justify-content-between align-items-center mb-2">
            <div style="width: 70px;">
                <ts-button class="btn-block" outline color="success" @click.prevent="exportExcel" :waiting="exporting">
                    {{ $t('excel') }}
                </ts-button>
            </div>

            <div class="d-flex">
                <select class="form-control mr-2" v-model="model.occupation">
                    <option :value="null">{{ $t('select') }}</option>
                    <option value="Student">{{ $t('eventManagement.student') }}</option>
                    <option value="UndergraduateStudent">{{ $t('eventManagement.UndergraduateStudent') }}</option>
                    <option value="Other">{{ $t('eventManagement.other') }}</option>
                </select>

                <select v-if="model.occupation === 'UndergraduateStudent'" class="form-control mr-2"
                    v-model="model.degree">
                    <option :value="null">{{ $t('select') }}</option>
                    <option value="Associate">Associate</option>
                    <option value="Bachelor">Bachelor</option>
                    <option value="Master">Master</option>
                </select>

                <ts-input v-model="searchDetail" :placeholder="$t('eventManagement.searchEvDetail')"
                    style="width: 300px;" />
            </div>
        </div>

        <!-- Table -->
        <ts-table :columns="columns" :records="filteredData" class="w-full mt-2">
            <template v-slot="{ record, index }">
                <td class="text-center px-2 py-1">{{ index + 1 }}</td>
                <td class="text-center px-2 py-1">{{ record.registration_no }}</td>

                <!-- Name: only this column becomes editable -->
                <td class="px-2 py-1">
                    <div v-if="isEditing(record)" class="d-flex align-items-center">
                        <ts-input v-model="editForm.name" size="small" class="mr-1"
                            @keyup.enter.native="saveEdit(record)" @keyup.esc.native="cancelEdit" />
                        <a href="#" title="save" class="text-success mr-2" @click.prevent="saveEdit(record)">
                            <i class="fas fa-check" :class="{ 'fa-spin fa-spinner': saving }"></i>
                        </a>
                        <a href="#" title="cancel" class="text-danger" @click.prevent="cancelEdit">
                            <i class="fas fa-times"></i>
                        </a>
                    </div>
                    <span v-else>{{ record.name }}</span>
                </td>

                <td class="text-center px-2 py-1">{{ record.sex }}</td>
                <td class="text-center px-2 py-1">{{ record.occupation }}</td>
                <td class="text-right px-2 py-1">{{ record.phone }}</td>
                <td class="text-center px-2 py-1">{{ record.degree }}</td>
                <td class="text-center px-2 py-1">{{ record.created_date }}</td>

                <!-- Actions -->
                <td class="text-center">
                    <a v-if="!isEditing(record)" href="#" title="edit" class="text-primary mr-2"
                        @click.prevent="edit(record)">
                        <i class="fas fa-edit"></i>
                    </a>
                </td>
            </template>
        </ts-table>

    </div>
</template>

<script>
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'
import { Errors } from 'form-backend-validation'

export default {
    name: 'viewStudentList',
    props: {
        value: Array,
        eventId: [String, Number]
    },
    data() {
        return {
            data: this.value || [],
            searchDetail: '',
            exporting: false,
            saving: false,
            editingKey: null,
            editForm: {
                name: ''
            },
            model: {
                event_mgt_id: '',
                occupation: null,
                degree: null
            },
            errors: new Errors()
        }
    },
    mounted() {
        this.model.event_mgt_id = this.eventId
        this.data = this.value || []
    },
    computed: {
        columns() {
            return [
                { name: this.$t('eventManagement.no'), style: 'text-align:center;width:50px;' },
                { name: this.$t('eventManagement.registeredNo'), style: 'text-align:center;width:100px;' },
                { name: this.$t('eventManagement.name'), style: 'width:200px;' },
                { name: this.$t('eventManagement.sex'), style: 'text-align:center;width:50px;' },
                { name: this.$t('eventManagement.occupation'), style: 'text-align:center;width:50px;' },
                { name: this.$t('eventManagement.phone'), style: 'text-align:right;width:120px;' },
                { name: this.$t('eventManagement.degree'), style: 'text-align:center;width:100px;' },
                { name: this.$t('eventManagement.registeredDate'), style: 'text-align:center;width:120px;' },
                {
                    name: this.$t('actions'),
                    style: 'width: 1px; text-align:center'
                }
            ]
        },
        filteredData() {
            let result = this.data;

            // Filter by occupation
            if (this.model.occupation) {
                result = result.filter(student => student.occupation === this.model.occupation);
            }

            // Filter by degree
            if (this.model.degree) {
                result = result.filter(student => student.degree === this.model.degree);
            }

            // Filter by search text
            if (this.searchDetail) {
                const search = this.searchDetail.toLowerCase();
                result = result.filter(student =>
                    (student.registration_no && student.registration_no.toLowerCase().includes(search)) ||
                    (student.name && student.name.toLowerCase().includes(search)) ||
                    (student.sex && student.sex.toLowerCase().includes(search)) ||
                    (student.phone && student.phone.toLowerCase().includes(search)) ||
                    (student.created_date && student.created_date.toLowerCase().includes(search))
                );
            }

            return result;
        }
    },
    methods: {
        // unique key helper — falls back to registration_no if record has no id
        rowKey(record) {
            return record.id !== undefined && record.id !== null ? record.id : record.registration_no
        },
        isEditing(record) {
            return this.editingKey !== null && this.editingKey === this.rowKey(record)
        },
        exportExcel() {
            this.exporting = true
            this.errors = new Errors()

            this.$store
                .dispatch('eventManagement/eventManagementDetailExcel', this.model)
                .then(response => {
                    let filename = trim(
                        response.headers['content-disposition']
                            .substring(
                                response.headers['content-disposition'].indexOf('filename')
                            )
                            .replace('filename=', ''),
                        '"'
                    )
                    FileDownload(response.data, filename)
                })
                .catch(err => {
                    let error = JSON.parse(
                        String.fromCharCode.apply(null, new Uint8Array(err))
                    )
                    this.$toasted.error(error.message)
                    this.errors = new Errors(error.errors)
                })
                .finally(() => (this.exporting = false))
        },
        edit(record) {
            this.editingKey = this.rowKey(record)
            this.editForm.name = record.name
        },
        cancelEdit() {
            this.editingKey = null
            this.editForm.name = ''
        },
        saveEdit(record) {
            if (!this.editForm.name || !this.editForm.name.trim()) {
                this.$toasted.error(this.$t('eventManagement.nameRequired'))
                return
            }

            this.saving = true
            this.errors = new Errors()

            this.$store
                .dispatch('eventManagement/updateRegisteredStudentName', {
                    registration_id: record.registration_id,
                    registration_no: record.registration_no,
                    name: this.editForm.name
                })
                .then(response => {
                    const key = this.rowKey(record)
                    const index = this.data.findIndex(d => this.rowKey(d) === key)
                    if (index !== -1) {
                        this.data.splice(index, 1, {
                            ...this.data[index],
                            name: this.editForm.name
                        })
                    }
                    this.$toasted.success(this.$t('Name updated successfully.'))
                    this.cancelEdit()
                })
                .catch(error => {
                    this.errors = new Errors(error.errors)
                    this.$toasted.error(error.message)
                })
                .finally(() => (this.saving = false))
        }
    },
    watch: {
        eventId(newVal) {
            this.model.event_mgt_id = newVal
        },
        value(newVal) {
            this.data = newVal || []
        },
        'model.occupation'(newVal) {
            if (newVal !== 'UndergraduateStudent') {
                this.model.degree = null
            }
        }

    }
}
</script>